import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  User, Phone, Calendar, MapPin, Plus, Trash2, Edit3,
  Star, Check, X, Home, Briefcase, ShieldCheck, AlertCircle
} from 'lucide-react';
import { authApi, addressApi, neighborhoodApi } from '../api/services';
import { useAuthStore } from '../store';

interface AddressItem {
  id: string;
  label: string;
  city: string;
  district: string;
  neighborhoodId?: string;
  neighborhoodName: string;
  street: string;
  buildingNo: string;
  buildingName?: string;
  floor?: string;
  apartmentNo?: string;
  description?: string;
  isDefault: boolean;
}

interface AddressFormState {
  id?: string;
  label: string;
  customLabel?: string;
  neighborhoodId: string;
  street: string;
  buildingNo: string;
  buildingName: string;
  floor: string;
  apartmentNo: string;
  description: string;
  isDefault: boolean;
}

const initialAddressForm: AddressFormState = {
  label: 'Ev',
  customLabel: '',
  neighborhoodId: '',
  street: '',
  buildingNo: '',
  buildingName: '',
  floor: '',
  apartmentNo: '',
  description: '',
  isDefault: false,
};

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user: authUser } = useAuthStore();

  // Profile data query
  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const res = await authApi.getMe();
        return res.data;
      } catch {
        return null;
      }
    },
  });

  // Addresses query
  const { data: addresses = [], isLoading: isAddressesLoading } = useQuery<AddressItem[]>({
    queryKey: ['addresses'],
    queryFn: () => addressApi.getAll().then((r) => r.data),
  });

  // Neighborhoods query for select box
  const { data: neighborhoods = [] } = useQuery({
    queryKey: ['neighborhoods'],
    queryFn: () => neighborhoodApi.getAll().then((r) => r.data),
  });

  // Modal states
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressFormState>(initialAddressForm);
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
  });

  // Delete address mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => addressApi.delete(id),
    onSuccess: () => {
      toast.success('Adres başarıyla silindi');
      qc.invalidateQueries({ queryKey: ['addresses'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Adres silinemedi.';
      toast.error(msg);
    },
  });

  // Set default address mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => addressApi.setDefault(id),
    onSuccess: () => {
      toast.success('Varsayılan adres güncellendi');
      qc.invalidateQueries({ queryKey: ['addresses'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Varsayılan adres ayarlanamadı.';
      toast.error(msg);
    },
  });

  // Save address (Create or Update)
  const saveAddressMutation = useMutation({
    mutationFn: async (form: AddressFormState) => {
      const n = neighborhoods.find((x: any) => x.id === form.neighborhoodId);
      if (!n) throw new Error('Lütfen geçerli bir mahalle seçin.');
      if (!form.street.trim() || !form.buildingNo.trim()) throw new Error('Cadde/Sokak ve Bina No zorunludur.');

      const finalLabel = form.label === 'Diğer' && form.customLabel?.trim()
        ? form.customLabel.trim()
        : form.label;

      const payload = {
        label: finalLabel,
        city: n.city || 'Isparta',
        district: n.district || 'Merkez',
        neighborhoodId: n.id,
        neighborhoodName: n.name,
        street: form.street.trim(),
        buildingNo: form.buildingNo.trim(),
        buildingName: form.buildingName?.trim() || null,
        floor: form.floor?.trim() || null,
        apartmentNo: form.apartmentNo?.trim() || null,
        description: form.description?.trim() || null,
        isDefault: form.isDefault || addresses.length === 0,
      };

      if (isEditingAddress && form.id) {
        return addressApi.update(form.id, payload);
      } else {
        return addressApi.create(payload);
      }
    },
    onSuccess: () => {
      toast.success(isEditingAddress ? 'Adres güncellendi' : 'Yeni adres eklendi');
      setIsAddressModalOpen(false);
      setAddressForm(initialAddressForm);
      qc.invalidateQueries({ queryKey: ['addresses'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'İşlem başarısız.';
      toast.error(msg);
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string }) => authApi.updateProfile(data),
    onSuccess: () => {
      toast.success('Profil bilgileri güncellendi');
      setIsProfileModalOpen(false);
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || 'Profil güncellenemedi.';
      toast.error(msg);
    },
  });

  // Open modal for editing
  const handleEditAddress = (addr: AddressItem) => {
    setIsEditingAddress(true);
    const isStandard = ['Ev', 'İş'].includes(addr.label);
    setAddressForm({
      id: addr.id,
      label: isStandard ? addr.label : 'Diğer',
      customLabel: isStandard ? '' : addr.label,
      neighborhoodId: addr.neighborhoodId || '',
      street: addr.street,
      buildingNo: addr.buildingNo,
      buildingName: addr.buildingName || '',
      floor: addr.floor || '',
      apartmentNo: addr.apartmentNo || '',
      description: addr.description || '',
      isDefault: addr.isDefault,
    });
    setIsAddressModalOpen(true);
  };

  // Open modal for creating new
  const handleAddNewAddress = () => {
    setIsEditingAddress(false);
    setAddressForm({
      ...initialAddressForm,
      neighborhoodId: neighborhoods[0]?.id || '',
      isDefault: addresses.length === 0,
    });
    setIsAddressModalOpen(true);
  };

  const handleOpenProfileModal = () => {
    setProfileForm({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
    });
    setIsProfileModalOpen(true);
  };

  const getLabelIcon = (label: string) => {
    const l = label.toLowerCase();
    if (l.includes('ev')) return <Home size={16} />;
    if (l.includes('iş') || l.includes('is') || l.includes('ofis')) return <Briefcase size={16} />;
    return <MapPin size={16} />;
  };

  const displayName = profile?.fullName || (authUser ? `Müşteri (${authUser.phone})` : 'Kullanıcı');
  const displayPhone = profile?.phone || authUser?.phone || '-';
  const displayRole = profile?.role === 'Admin' ? 'Yönetici' : profile?.role === 'Operator' ? 'Operatör' : 'Müşteri';

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px', minHeight: '80vh' }}>
      {/* ─── Profile Hero Card ─── */}
      <div
        className="card"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)',
          border: '1px solid #fed7aa',
          borderRadius: 20,
          padding: '28px',
          marginBottom: '32px',
          boxShadow: '0 8px 30px rgba(255, 94, 20, 0.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--color-primary), #ea580c)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.8rem',
              fontWeight: 800,
              boxShadow: '0 6px 20px rgba(255, 94, 20, 0.3)',
            }}
          >
            {profile?.firstName ? profile.firstName[0].toUpperCase() : <User size={34} />}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, color: 'var(--color-text)' }}>
                {displayName}
              </h1>
              <span
                style={{
                  background: '#ffedd5',
                  color: '#c2410c',
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <ShieldCheck size={13} /> {displayRole}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Phone size={15} color="var(--color-primary)" /> {displayPhone}
              </span>
              {profile?.createdAt && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Calendar size={15} color="var(--color-text-secondary)" />
                  Üyelik: {new Date(profile.createdAt).toLocaleDateString('tr-TR')}
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleOpenProfileModal}
          className="btn btn-secondary btn-sm"
          style={{ borderRadius: 10, padding: '8px 16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Edit3 size={15} /> Profili Düzenle
        </button>
      </div>

      {/* ─── Addresses Section ─── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin size={22} color="var(--color-primary)" /> Kayıtlı Adreslerim
            </h2>
            <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>
              Siparişlerinizin teslim edileceği adreslerinizi buradan yönetebilirsiniz.
            </p>
          </div>
          <button
            onClick={handleAddNewAddress}
            className="btn btn-primary"
            style={{ borderRadius: 12, padding: '10px 20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <Plus size={18} /> Yeni Adres Ekle
          </button>
        </div>

        {/* Address Cards Grid */}
        {isAddressesLoading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            Adresler yükleniyor...
          </div>
        ) : addresses.length === 0 ? (
          <div
            className="card"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              borderRadius: 16,
              background: '#fafaf9',
              border: '2px dashed #e7e5e4',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#fee2e2',
                color: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <MapPin size={30} />
            </div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 8 }}>Henüz Kayıtlı Adresiniz Yok</h3>
            <p className="text-muted" style={{ maxWidth: 440, margin: '0 auto 20px', fontSize: '0.9rem' }}>
              Yemek ve market siparişlerinizi kapınıza kadar hızlıca ulaştırmamız için hemen bir teslimat adresi tanımlayın.
            </p>
            <button onClick={handleAddNewAddress} className="btn btn-primary">
              <Plus size={16} /> İlk Adresinizi Ekleyin
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {addresses.map((addr) => (
              <div
                key={addr.id}
                className="card"
                style={{
                  borderRadius: 16,
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: addr.isDefault ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                  background: addr.isDefault ? '#fffbf7' : 'var(--color-surface)',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  boxShadow: addr.isDefault ? '0 4px 20px rgba(255, 94, 20, 0.12)' : 'none',
                }}
              >
                <div>
                  {/* Card Top: Label & Default Badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: addr.isDefault ? 'var(--color-primary)' : 'var(--color-text)',
                      }}
                    >
                      {getLabelIcon(addr.label)} {addr.label}
                    </span>
                    {addr.isDefault && (
                      <span
                        style={{
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          color: 'white',
                          padding: '3px 9px',
                          borderRadius: 999,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                        }}
                      >
                        <Check size={12} /> Varsayılan Adres
                      </span>
                    )}
                  </div>

                  {/* Neighborhood, Street, Building */}
                  <div style={{ fontSize: '0.92rem', color: 'var(--color-text)', lineHeight: 1.5, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600 }}>{addr.neighborhoodName} Mah.</div>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.88rem' }}>
                      {addr.street} No:{addr.buildingNo}{addr.buildingName ? ` ${addr.buildingName}` : ''}{addr.floor ? ` Kat:${addr.floor}` : ''}{addr.apartmentNo ? ` Daire:${addr.apartmentNo}` : ''}
                    </div>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem', marginTop: 2 }}>
                      {addr.district} / {addr.city}
                    </div>
                  </div>

                  {/* Description / Instructions */}
                  {addr.description && (
                    <div
                      style={{
                        background: 'rgba(0,0,0,0.03)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: '0.8rem',
                        color: 'var(--color-text-secondary)',
                        fontStyle: 'italic',
                        marginBottom: 12,
                      }}
                    >
                      📝 {addr.description}
                    </div>
                  )}
                </div>

                {/* Card Actions Footer */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 14,
                    borderTop: '1px solid var(--color-border)',
                    marginTop: 10,
                  }}
                >
                  <div>
                    {!addr.isDefault ? (
                      <button
                        onClick={() => setDefaultMutation.mutate(addr.id)}
                        disabled={setDefaultMutation.isPending}
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '4px 8px', fontSize: '0.8rem', color: 'var(--color-primary)', fontWeight: 600 }}
                      >
                        <Star size={14} /> Varsayılan Yap
                      </button>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <Star size={13} fill="#10b981" /> Seçili Adres
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => handleEditAddress(addr)}
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '6px 10px', borderRadius: 8 }}
                      title="Düzenle"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`"${addr.label}" adresini silmek istediğinize emin misiniz?`)) {
                          deleteMutation.mutate(addr.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="btn btn-ghost btn-sm text-danger"
                      style={{ padding: '6px 10px', borderRadius: 8 }}
                      title="Sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Add / Edit Address Modal ─── */}
      {isAddressModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 20,
              padding: '28px',
              background: 'white',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>
                {isEditingAddress ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
              </h3>
              <button
                onClick={() => setIsAddressModalOpen(false)}
                className="btn btn-ghost btn-sm"
                style={{ padding: 6, borderRadius: '50%' }}
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveAddressMutation.mutate(addressForm);
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              {/* Adres Başlığı Seçimi */}
              <div className="form-group">
                <label className="form-label">Adres Başlığı</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  {['Ev', 'İş', 'Diğer'].map((lbl) => (
                    <button
                      key={lbl}
                      type="button"
                      onClick={() => setAddressForm({ ...addressForm, label: lbl })}
                      className={`btn btn-sm ${addressForm.label === lbl ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex: 1, borderRadius: 10 }}
                    >
                      {lbl === 'Ev' && <Home size={14} />}
                      {lbl === 'İş' && <Briefcase size={14} />}
                      {lbl === 'Diğer' && <MapPin size={14} />}
                      {lbl}
                    </button>
                  ))}
                </div>
                {addressForm.label === 'Diğer' && (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Örn: Anne Evi, Yazlık, Spor Salonu"
                    value={addressForm.customLabel}
                    onChange={(e) => setAddressForm({ ...addressForm, customLabel: e.target.value })}
                    required
                  />
                )}
              </div>

              {/* İl / İlçe */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">İl</label>
                  <input type="text" className="form-input" value="Isparta" disabled style={{ background: '#f5f5f4' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">İlçe</label>
                  <input type="text" className="form-input" value="Merkez" disabled style={{ background: '#f5f5f4' }} />
                </div>
              </div>

              {/* Mahalle Seçimi */}
              <div className="form-group">
                <label className="form-label">Mahalle *</label>
                <select
                  className="form-input"
                  value={addressForm.neighborhoodId}
                  onChange={(e) => setAddressForm({ ...addressForm, neighborhoodId: e.target.value })}
                  required
                >
                  <option value="">Mahalle Seçiniz</option>
                  {neighborhoods.map((n: any) => (
                    <option key={n.id} value={n.id}>
                      {n.name} (Teslimat: ₺{n.deliveryFee})
                    </option>
                  ))}
                </select>
              </div>

              {/* Cadde / Sokak */}
              <div className="form-group">
                <label className="form-label">Cadde / Sokak *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Örn: 102. Cadde, Atatürk Bulvarı"
                  value={addressForm.street}
                  onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                  required
                />
              </div>

              {/* Bina No & Apartman / Bina İsmi */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Bina No *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Örn: 14"
                    value={addressForm.buildingNo}
                    onChange={(e) => setAddressForm({ ...addressForm, buildingNo: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Apartman / Bina İsmi</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Örn: Kardelen Apt."
                    value={addressForm.buildingName}
                    onChange={(e) => setAddressForm({ ...addressForm, buildingName: e.target.value })}
                  />
                </div>
              </div>

              {/* Kat & Daire No */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Kat</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Örn: 2, 3, Zemin"
                    value={addressForm.floor}
                    onChange={(e) => setAddressForm({ ...addressForm, floor: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Daire No</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Örn: 5, 12"
                    value={addressForm.apartmentNo}
                    onChange={(e) => setAddressForm({ ...addressForm, apartmentNo: e.target.value })}
                  />
                </div>
              </div>

              {/* Adres Notu / Tarifi */}
              <div className="form-group">
                <label className="form-label">Adres Tarifi / Kurye Notu</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Örn: Sarı bina, marketin yanı, zili çalmayın"
                  value={addressForm.description}
                  onChange={(e) => setAddressForm({ ...addressForm, description: e.target.value })}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Varsayılan Yap Checkbox */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem', margin: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={addressForm.isDefault}
                  onChange={(e) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                />
                <span>Bu adresi <strong>varsayılan teslimat adresim</strong> yap</span>
              </label>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setIsAddressModalOpen(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1, borderRadius: 12 }}
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={saveAddressMutation.isPending}
                  className="btn btn-primary"
                  style={{ flex: 2, borderRadius: 12 }}
                >
                  {saveAddressMutation.isPending ? 'Kaydediliyor...' : 'Adresi Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Edit Profile Modal ─── */}
      {isProfileModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: 440,
              borderRadius: 20,
              padding: '28px',
              background: 'white',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>Profil Bilgilerini Düzenle</h3>
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="btn btn-ghost btn-sm"
                style={{ padding: 6, borderRadius: '50%' }}
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateProfileMutation.mutate(profileForm);
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              <div className="form-group">
                <label className="form-label">Adınız *</label>
                <input
                  type="text"
                  className="form-input"
                  value={profileForm.firstName}
                  onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                  placeholder="Adınız"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Soyadınız *</label>
                <input
                  type="text"
                  className="form-input"
                  value={profileForm.lastName}
                  onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                  placeholder="Soyadınız"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Telefon Numarası</label>
                <input
                  type="text"
                  className="form-input"
                  value={displayPhone}
                  disabled
                  style={{ background: '#f5f5f4' }}
                />
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Telefon numarası güvenlik sebebiyle değiştirilemez.
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => setIsProfileModalOpen(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1, borderRadius: 12 }}
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isPending}
                  className="btn btn-primary"
                  style={{ flex: 2, borderRadius: 12 }}
                >
                  {updateProfileMutation.isPending ? 'Kaydediliyor...' : 'Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
